/**
 * Which evaluator version each experiment was scored with, and what it takes
 * to line them up: re-score every experiment behind the newest version.
 */

import type { ExperimentSummary } from "@/features/experiments/types";
import type { CompareEvaluator } from "./types";

export interface VersionGap {
  evaluator: CompareEvaluator;
  /** The newest version any experiment here was scored with. */
  target: number;
  /** "v2 in A, v3 in B" — each version with the experiments scored with it. */
  groups: Array<{ version: number | null; names: string[] }>;
  /** Experiments to re-score with `target`. */
  behind: ExperimentSummary[];
}

export function versionGaps(evaluators: CompareEvaluator[], experiments: ExperimentSummary[]): VersionGap[] {
  return evaluators.flatMap((evaluator) => {
    const versions = evaluator.versions;
    if (!evaluator.version_mismatch || !versions) return [];
    const target = Math.max(...Object.values(versions).map((v) => v ?? 0));
    const groups = new Map<number | null, string[]>();
    for (const e of experiments) {
      const v = versions[e.id] ?? null;
      groups.set(v, [...(groups.get(v) ?? []), e.name]);
    }
    return [
      {
        evaluator,
        target,
        groups: [...groups.entries()]
          .sort(([a], [b]) => (a ?? 0) - (b ?? 0))
          .map(([version, names]) => ({ version, names })),
        behind: experiments.filter((e) => (versions[e.id] ?? 0) < target),
      },
    ];
  });
}

const list = (names: string[]) =>
  names.length < 2 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/** "Correctness is v2 in Prompt v13, v3 in Prompt v14". */
export function gapSentence(gap: VersionGap): string {
  const parts = gap.groups.map(
    (g) => `${g.version === null ? "not scored" : `v${g.version}`} in ${list(g.names)}`,
  );
  return `${gap.evaluator.name} is ${parts.join(", ")}`;
}
